// Copyright (c) Docugami, Inc. All rights reserved.

import { clamp, lerpValue } from "./MathUtils";

/**
 * Takes a hex color string and does a simple color adjustment based on amount
 * @param color The hex color string to adjust
 * @param amount Percentage amount to adjust each value toward white white or black
 */
export function adjustColor(color: string, amount: number) {
    return '#' + color.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
}

function blendColorValue(a: number, b: number, t: number) {
    t = clamp(t, 0, 1);
    return Math.sqrt(((1 - t) * Math.pow(a, 2)) + (t * Math.pow(b, 2)));
}

export function blendColors(color1: string, color2: string, t: number): Color {
    const c1 = new Color(color1);
    const c2 = new Color(color2);

    const newColor = new Color();
    newColor.r = Math.round(blendColorValue(c1.r, c2.r, t));
    newColor.g = Math.round(blendColorValue(c1.g, c2.g, t));
    newColor.b = Math.round(blendColorValue(c1.b, c2.b, t));
    newColor.a = lerpValue(c1.a, c2.a, t);

    return newColor;
}

export function lerpColor(color1: string, color2: string, t: number): Color {
    const c1 = new Color(color1);
    const c2 = new Color(color2);

    const newColor = new Color();
    newColor.r = Math.round(lerpValue(c1.r, c2.r, t));
    newColor.g = Math.round(lerpValue(c1.g, c2.g, t));
    newColor.b = Math.round(lerpValue(c1.b, c2.b, t));
    newColor.a = lerpValue(c1.a, c2.a, t);

    return newColor;
}

export interface IColor {
    r: number;
    g: number;
    b: number;
    a: number;
    toHexString(): string;
    toRgbString(): string;
    toRgbaString(): string;
}

const RGB_COLOR_REGEX = /\((\d+),\s*(\d+),\s*(\d+)(,\s*(\d*.\d*))?\)/;
export class Color implements IColor {
    // 0 - 255
    r: number = 0;

    // 0 - 255
    g: number = 0;

    // 0 - 255
    b: number = 0;

    // 0 - 1
    a: number = 1;

    constructor()
    constructor(colorStr?: string)
    constructor(r?: string|number, g?: number, b?: number)
    constructor(r?: string|number, g?: number, b?: number, a?: number) {
        if (typeof r === 'string') {
            r = r.trim();
            if (r.startsWith('#')) {
                if (r.length > 6) {
                    this.r = parseInt(r.substring(1, 3), 16);
                    this.g = parseInt(r.substring(3, 5), 16);
                    this.b = parseInt(r.substring(5, 7), 16);

                    if (r.length > 8) {
                        this.a = parseInt(r.substring(7, 9), 16) / 255;
                    }
                }
                else {
                    throw new Error("Invalid input string");
                }
            }
            else if (r.startsWith('rgb')) {
                const res = RGB_COLOR_REGEX.exec(r);
                this.r = parseInt(res![1], 10);
                this.g = parseInt(res![2], 10);
                this.b = parseInt(res![3], 10);
                this.a = res![5] ? parseFloat(res![5]) : 1;
            }
            else {
                throw new Error("Invalid input string")
            }
        }
        else {
            this.r = r ?? 0;
            this.g = g ?? 0;
            this.b = b ?? 0;
            this.a = a ?? 1;
        }
    }

    toHexString() {
        return `#${this.r.toString(16)}${this.g.toString(16)}${this.b.toString(16)}${(Math.round(this.a * 255)).toString(16)}`.toUpperCase();
    }

    toRgbString() {
        return `rgb(${this.r}, ${this.g}, ${this.b})`;
    }

    toRgbaString() {
        return `rgba(${this.r}, ${this.g}, ${this.b}, ${this.a})`;
    }
}
